import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as firebaseCourses from '../../services/firebaseCourses';
import { sendCourseMessage } from '../../services/courseMessages';
import { formatCoursePrice } from '../../utils/priceUtils';
import './Payment.css';

const BANK_ACCOUNT_NUMBER = '0108209430';
const BANK_NAME = 'Access Bank';
const ACCOUNT_NAME = 'John Nnoruka';

export default function Payment() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: `/course/${courseId}/pay` } });
      return;
    }
    let isMounted = true;
    setLoading(true);
    firebaseCourses.getCourse(courseId)
      .then((detail) => {
        if (isMounted) setCourse(detail || null);
      })
      .catch(() => {
        if (isMounted) setCourse(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, [courseId, isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!course) return;
    setError('');
    const trimmed = message.trim();
    if (!trimmed) {
      setError('Please type a message before sending.');
      return;
    }
    setSubmitting(true);
    try {
      await sendCourseMessage({
        courseId,
        courseTitle: course.title || '',
        userId: user.uid,
        userEmail: user.email || '',
        userName: user.name || '',
        message: trimmed,
      });
      navigate('/my-learning', { state: { paymentMessageSent: true } });
    } catch (err) {
      setError(err?.message || 'Could not send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !course) {
    return (
      <div className="payment-page">
        <div className="payment-inner">
          <p className="payment-loading">Loading…</p>
        </div>
      </div>
    );
  }

  const priceDisplay = formatCoursePrice(course.price);

  return (
    <div className="payment-page">
      <div className="payment-inner">
        <h1 className="payment-title">Pay for course</h1>
        <p className="payment-course-name">{course.title}</p>
        <section className="payment-bank">
          <h2 className="payment-section-title">Bank transfer details</h2>
          <p className="payment-instruction">
            Transfer the course amount to the account below. After payment, send a message to the course owner so they can
            confirm and you’ll have full access in My Learning.
          </p>
          <div className="payment-bank-card">
            <dl className="payment-bank-details">
              <dt>Account number</dt>
              <dd>
                <strong>{BANK_ACCOUNT_NUMBER}</strong>
              </dd>
              <dt>Bank</dt>
              <dd>{BANK_NAME}</dd>
              <dt>Account name</dt>
              <dd>{ACCOUNT_NAME}</dd>
              <dt>Amount</dt>
              <dd className="payment-amount">{priceDisplay}</dd>
            </dl>
          </div>
        </section>

        <section className="payment-message">
          <h2 className="payment-section-title">Message to course owner</h2>
          <p className="payment-message-hint">
            After you’ve paid, send a short message (e.g. your payment reference or “I have paid”). The course owner will
            receive your message, respond, and then activate your access. The course will appear in My Learning once
            activated.
          </p>
          <form onSubmit={handleSubmit} className="payment-form">
            {error && <p className="payment-error" role="alert">{error}</p>}
            <label className="payment-label">
              <span className="payment-label-text">Your message</span>
              <textarea
                className="payment-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask a question or share your payment reference so the course owner can assist you."
                rows={4}
              />
            </label>
            <div className="payment-actions">
              <button type="submit" className="payment-btn payment-btn-primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send message'}
              </button>
              <Link to={`/course/${courseId}/overview`} className="payment-btn payment-btn-outline">Back to course</Link>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
